import React, { Fragment } from 'react';
import { Breakpoint } from 'react-socks';
import { common } from '@kurtosys/ksys-app-template';
import { observer } from 'mobx-react';

import { factHasValue } from '../../../shared/factHasValue.js';
import { InjectedStyledComponent, ResponsiveComponentBase } from '../../../shared/index.js';
import { ISummaryBlockProps } from '../SummaryBlock/models/ISummaryBlockProps.js';

import { ISummaryBlocksConfiguration, ISummaryBlocksProps, ISummaryBlocksState } from './models/index.js';
import Block from './styledComponents/Block.js';
import Row from './styledComponents/Row.js';
import Wrapper from './styledComponents/Wrapper.js';
import { SUMMARY_BLOCKS_CATEGORY_KEY, SUMMARY_BLOCKS_COMPONENT_KEY } from './SummaryBlocks.meta.js';

export class SummaryBlocks extends ResponsiveComponentBase<
	ISummaryBlocksProps,
	ISummaryBlocksState,
	ISummaryBlocksConfiguration
> {
	categoryKey = SUMMARY_BLOCKS_CATEGORY_KEY;
	componentKey = SUMMARY_BLOCKS_COMPONENT_KEY;

	constructor(props: ISummaryBlocksProps) {
		super(props);
	}

	getOrderedBlocks(props: ISummaryBlocksProps): ISummaryBlockProps[] {
		const { blocks = [], order = [] } = props;
		// Filter out any block that has the hide property set,
		// this will allow the blocks that do render fill out the items per row amount
		const workingCollection = [...blocks.filter(block => !block.hide)];
		const response: ISummaryBlockProps[] = [];
		for (const key of order) {
			for (let i = 0; i < workingCollection.length; i++) {
				const block = workingCollection[i];
				if (block.key === key) {
					response.push(block);
					// remove the block from the working collection to append any remaining blocks
					workingCollection.splice(i, 1);
					i--;
				}
			}
		}
		response.push(...workingCollection);
		return response;
	}

	getRows = (props: ISummaryBlocksProps) => {
		const { itemsPerRow = 3, lineBreaks } = props;
		const isLineBreak = (block: ISummaryBlockProps) => {
			if (!lineBreaks || lineBreaks.keys.length <= 0) {
				return false;
			}
			const { field, keys } = lineBreaks;
			const value = (block as any)[field];
			return keys.filter((key) => key.toLowerCase() === value.toLowerCase()).length > 0;
		};
		const blocks = this.getOrderedBlocks(props);
		return common.commonUtils.split(blocks, itemsPerRow, isLineBreak);
	};

	getBlocks = (row: ISummaryBlockProps[], props: ISummaryBlocksProps) => {
		const {
			placeholder = '-',
			itemsPerRow = 3,
			onDocumentClick,
			accessibilityGroupText,
			includeTitleInAccessibilityGroup,
		} = props;
		return row.map((block: ISummaryBlockProps, index: number) => {
			const { value, ...blockProps } = block;
			if (!blockProps.onDocumentClick && onDocumentClick) {
				blockProps.onDocumentClick = onDocumentClick;
			}
			// Exit out if no value and show blank is set to true
			if (!factHasValue(block)) {
				const { blankDataOptions } = block;
				if (blankDataOptions && !blankDataOptions.showBlank) {
					return;
				}
			}
			// Only set if it has not been configured
			if (accessibilityGroupText && !blockProps.factAccessibilityGroupText) {
				blockProps.factAccessibilityGroupText = accessibilityGroupText;
			}
			// due to the boolean type, we need to check if undefined to be able to push false on the block props
			if (
				includeTitleInAccessibilityGroup !== undefined &&
				blockProps.includeTitleInAccessibilityGroup === undefined
			) {
				blockProps.includeTitleInAccessibilityGroup = includeTitleInAccessibilityGroup;
			}
			return (
				<Block
					key={`block_${index}`}
					value={common.commonUtils.isNullOrUndefined(value) ? placeholder : value}
					basis={`${100 / itemsPerRow}%`}
					{...blockProps}
				/>
			);
		});
	};

	render() {
		const breakpoints = this.breakpoints;
		return (
			<Fragment>
				{breakpoints.map((breakpoint, breakpointIndex) => {
					const { props } = breakpoint;
					if (props) {
						const breakpointProps = this.getBreakpointComponentProps(breakpoint, breakpointIndex);
						const rows = this.getRows(props as ISummaryBlocksProps);
						const { className } = props;
						return (
							<Breakpoint {...breakpointProps}>
								<Wrapper className={className}>
									{(rows || []).map((row: ISummaryBlockProps[], index: number) => {
										return <Row key={`row_${index}`}>{this.getBlocks(row, props)}</Row>;
									})}
								</Wrapper>
							</Breakpoint>
						);
					}
				})}
			</Fragment>
		);
	}
}

export default InjectedStyledComponent(
	observer(SummaryBlocks),
	SUMMARY_BLOCKS_CATEGORY_KEY,
	SUMMARY_BLOCKS_COMPONENT_KEY,
);
